import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Tooltip,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import GroupIcon from '@mui/icons-material/Group';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import BadgeIcon from '@mui/icons-material/Badge';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';

import {
  useFetchPermisos,
  useFetchRoles,
  useCreateRol,
  useUpdateRol,
  useDeleteRol,
  useFetchUsuariosERP,
  useFetchUsuariosERPList,
  useAsignarRolUsuario,
  useEliminarRolUsuario,
  useAsignarAreaUsuario,
  useActualizarAreaUsuario,
  useEliminarAreaUsuario,
  useFetchTicketAreas,
  useFetchUsuarioResumen,
  useFetchUsuarioModulos,
  useUpdateUsuarioModulos,
  useSincronizarDatos,
} from '@/shared/actions/erp/tickets-soporte-cliente';

import { useFetchModulosSistema } from '@/shared/actions/erp';

import type {
  IModuloPermiso,
  IModuloRol,
  IUsuarioERP,
} from '@/shared/interfaces/erp/tickets-soporte-clientes';

// ---- Group permissions by module ----
function groupByModulo(permisos: IModuloPermiso[]) {
  const groups: Record<string, IModuloPermiso[]> = {};
  for (const p of permisos) {
    if (!groups[p.modulo]) groups[p.modulo] = [];
    groups[p.modulo].push(p);
  }
  return groups;
}

const MODULO_LABELS: Record<string, string> = {
  tickets: 'Tickets',
  masivos: 'Daños Masivos',
  agenda: 'Agenda Técnicos',
  catalogos: 'Catálogos',
  trazabilidad: 'Trazabilidad',
  reportes: 'Reportes',
  admin: 'Administración',
  operaciones: 'Operaciones',
  // Manual / base de conocimiento de tickets: un check por audiencia
  // (Usuario, Supervisor, Admin TI) + edición.
  documentacion: 'Base de conocimiento',
};

const ERP_USUARIOS_EDIT_URL = '/parametrizacion/usuarios/editar';

function SyncButton() {
  const syncMutation = useSincronizarDatos();
  return (
    <Tooltip title="Forzar sincronización de usuarios y datos del ERP">
      <IconButton
        color="primary"
        onClick={() => syncMutation.mutate()}
        disabled={syncMutation.isPending}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
      >
        {syncMutation.isPending ? <CircularProgress size={20} /> : <SyncIcon />}
      </IconButton>
    </Tooltip>
  );
}

export default function GestionAccesosPage() {
  const [tabValue, setTabValue] = useState(0);

  return (
    <Box sx={{ mt: 2, mx: { xs: 0, md: 2 } }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" fontWeight={700} color="primary.main">
          Gestión de Accesos
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Administre roles, permisos y asignaciones de usuarios para los módulos del sistema.
        </Typography>
      </Paper>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<SecurityIcon />} iconPosition="start" label="Roles" />
          <Tab icon={<GroupIcon />} iconPosition="start" label="Usuarios y Roles" />
          <Tab icon={<BadgeIcon />} iconPosition="start" label="Usuarios y Áreas" />
          <Tab icon={<ViewModuleIcon />} iconPosition="start" label="Módulos del Sistema" />
        </Tabs>
      </Paper>

      {tabValue === 0 && <TabRoles />}
      {tabValue === 1 && <TabUsuariosRoles />}
      {tabValue === 2 && <TabUsuariosAreas />}
      {tabValue === 3 && <TabModulos />}
    </Box>
  );
}

// ===========================================================================
// USER DETAIL DIALOG - Shows full ERP user data + roles/perms/areas
// ===========================================================================

function UserDetailDialog({
  open,
  onClose,
  userId,
  username,
  nombreCompleto,
}: {
  open: boolean;
  onClose: () => void;
  userId: number | null;
  username: string;
  nombreCompleto: string;
}) {
  const { data: usersRes, isLoading: isLoadingUser } = useFetchUsuariosERP(
    username,
    open && !!username && username.length >= 2
  );
  const { data: resumenRes, isLoading: isLoadingResumen } = useFetchUsuarioResumen(
    userId || 0,
    open && !!userId
  );

  const rawUsers = (usersRes as any)?.data || usersRes;
  const usuarios: IUsuarioERP[] = rawUsers?.data?.data || rawUsers?.data || [];
  const user = usuarios.find((u) => u.id === userId) || usuarios[0] || null;

  const resumen = (resumenRes as any)?.data?.data || (resumenRes as any)?.data || null;

  const { data: rolesRes } = useFetchRoles();
  const roles: IModuloRol[] = (rolesRes as any)?.data?.data || (rolesRes as any)?.data || [];

  const rolesDelUsuario = resumen?.roles
    ? roles.filter((r) => resumen.roles.includes(r.id_rol))
    : [];

  const isLoading = isLoadingUser || isLoadingResumen;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonIcon color="primary" />
            <Typography variant="h6" fontWeight={700}>
              {nombreCompleto || username}
            </Typography>
          </Stack>
          {user && (
            <Chip
              label={user.is_active ? 'Activo' : 'Inactivo'}
              size="small"
              color={user.is_active ? 'success' : 'default'}
              variant="outlined"
            />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !user ? (
          <Alert severity="info">No se encontraron datos del usuario en el ERP.</Alert>
        ) : (
          <Stack spacing={3}>
            {/* ---- Datos de Usuario ---- */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                Datos de Usuario
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DetailField label="Username" value={user.username} icon={<PersonIcon fontSize="small" />} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Email" value={user.email} icon={<EmailIcon fontSize="small" />} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Razón Social" value={user.razon_social} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Identificación" value={`${user.tipo_identificacion}: ${user.identificacion}`} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Rol del Sistema" value={user.rol_sistema} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField
                    label="Estado"
                    value={user.is_active ? 'Activo' : 'Inactivo'}
                    chipColor={user.is_active ? 'success' : 'default'}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* ---- Datos de Empresa ---- */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                Datos de Empresa / Organización
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DetailField label="Empresa" value={user.empresa} icon={<BusinessIcon fontSize="small" />} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Área" value={user.area} icon={<AccountTreeIcon fontSize="small" />} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Departamento" value={user.departamento} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DetailField label="Canal de Venta" value={user.canal_venta} />
                </Grid>
                {user.canales_adicionales?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Canales Adicionales</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {user.canales_adicionales.map((c: any, i: number) => (
                        <Chip key={i} label={c?.nombre || c} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Grid>
                )}
                {user.departamentos_adicionales?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Departamentos Adicionales</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {user.departamentos_adicionales.map((d: any, i: number) => (
                        <Chip key={i} label={d?.nombre || d} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Divider />

            {/* ---- Roles y Permisos del Módulo ---- */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                Roles y Permisos (Módulo Tickets)
              </Typography>
              {rolesDelUsuario.length > 0 ? (
                <Stack spacing={1}>
                  {rolesDelUsuario.map((rol) => (
                    <Stack key={rol.id_rol} direction="row" spacing={1} alignItems="center">
                      <Chip label={rol.nombre} size="small" color="primary" variant="outlined" />
                      <Typography variant="caption" color="text.secondary">
                        ({rol.permisos_count ?? rol.permisos?.length ?? 0} permisos)
                      </Typography>
                      {rol.es_predefinido && (
                        <Chip label="Predefinido" size="small" color="info" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                      )}
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">Sin roles asignados en este módulo</Typography>
              )}

              {resumen?.permisos?.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Permisos efectivos: {resumen.permisos.length}
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    {resumen.permisos.slice(0, 15).map((p: string) => (
                      <Chip key={p} label={p} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    ))}
                    {resumen.permisos.length > 15 && (
                      <Chip label={`+${resumen.permisos.length - 15} más`} size="small" color="default" />
                    )}
                  </Stack>
                </Box>
              )}
            </Box>

            <Divider />

            {/* ---- Áreas Asignadas ---- */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                Áreas Asignadas
              </Typography>
              {resumen?.areas?.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {resumen.areas.map((a: any) => (
                    <Chip
                      key={a.id}
                      label={`${a.nombre}${a.es_responsable ? ' (Responsable)' : ''}${a.es_configuracion ? ' (Configuración)' : ''}`}
                      size="small"
                      color={a.es_responsable ? 'success' : a.es_configuracion ? 'info' : 'default'}
                      variant="outlined"
                    />
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">Sin áreas asignadas</Typography>
              )}
            </Box>

            {/* ---- Otros datos ---- */}
            {(user.cantidad_consultas > 0 || user.consultas_realizadas > 0) && (
              <>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                    Otros
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <DetailField label="Consultas Permitidas" value={String(user.cantidad_consultas ?? 0)} />
                    </Grid>
                    <Grid item xs={6}>
                      <DetailField label="Consultas Realizadas" value={String(user.consultas_realizadas ?? 0)} />
                    </Grid>
                  </Grid>
                </Box>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          href={`${ERP_USUARIOS_EDIT_URL}/${userId}`}
          target="_blank"
          disabled={!userId}
        >
          Editar en ERP
        </Button>
        <Button onClick={onClose} color="inherit">Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailField({
  label,
  value,
  icon,
  chipColor,
}: {
  label: string;
  value?: string;
  icon?: React.ReactNode;
  chipColor?: 'success' | 'default' | 'error' | 'info' | 'warning' | 'primary' | 'secondary';
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {icon}
        {chipColor ? (
          <Chip label={value || '-'} size="small" color={chipColor} variant="outlined" />
        ) : (
          <Typography variant="body2" fontWeight={500}>{value || '-'}</Typography>
        )}
      </Stack>
    </Box>
  );
}

// ===========================================================================
// TAB 0: Roles
// ===========================================================================

function TabRoles() {
  const { data: rolesRes, isLoading } = useFetchRoles();
  const { data: permisosRes } = useFetchPermisos();
  const deleteMutation = useDeleteRol();
  const [editRol, setEditRol] = useState<IModuloRol | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<IModuloRol | null>(null);

  const roles: IModuloRol[] = (rolesRes as any)?.data?.data || (rolesRes as any)?.data || [];
  const permisos: IModuloPermiso[] = (permisosRes as any)?.data?.data || (permisosRes as any)?.data || [];
  const permisosGrouped = useMemo(() => groupByModulo(permisos), [permisos]);

  const handleNew = () => {
    setEditRol(null);
    setOpenDialog(true);
  };

  const handleEdit = (rol: IModuloRol) => {
    setEditRol(rol);
    setOpenDialog(true);
  };

  const handleDelete = () => {
    if (!deleteConfirm) return;
    deleteMutation.mutate(deleteConfirm.id_rol, {
      onSuccess: () => setDeleteConfirm(null),
    });
  };

  // Get module breakdown for a role
  const getModuleBreakdown = (rol: IModuloRol) => {
    if (!rol.permisos_data?.length) return [];
    const grouped = groupByModulo(rol.permisos_data);
    return Object.entries(grouped).map(([mod, perms]) => ({
      modulo: mod,
      label: MODULO_LABELS[mod] || mod,
      count: perms.length,
    }));
  };

  if (isLoading) return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Chip label={`${roles.length} roles`} color="primary" variant="outlined" />
        <Stack direction="row" spacing={1}>
          <SyncButton />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
            Nuevo Rol
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        {roles.map((rol) => {
          const moduleBreakdown = getModuleBreakdown(rol);
          return (
            <Grid item xs={12} md={6} lg={4} key={rol.id_rol}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight={700}>{rol.nombre}</Typography>
                    <Stack direction="row" spacing={0.5}>
                      {rol.es_predefinido && <Chip label="Predefinido" size="small" color="info" variant="outlined" />}
                      <Chip label={rol.activo ? 'Activo' : 'Inactivo'} size="small" color={rol.activo ? 'success' : 'default'} variant="outlined" />
                    </Stack>
                  </Stack>

                  {rol.codigo && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {rol.codigo}
                    </Typography>
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                    {rol.descripcion || 'Sin descripción'}
                  </Typography>

                  <Chip
                    label={`${rol.permisos_count ?? rol.permisos_data?.length ?? rol.permisos?.length ?? 0} permisos`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />

                  {/* Module breakdown */}
                  {moduleBreakdown.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1, gap: 0.5 }}>
                      {moduleBreakdown.map((m) => (
                        <Chip
                          key={m.modulo}
                          label={`${m.label}: ${m.count}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.65rem' }}
                        />
                      ))}
                    </Stack>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between' }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(rol)}>
                    Editar
                  </Button>
                  {!rol.es_predefinido && (
                    <Tooltip title="Eliminar rol">
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirm(rol)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <RolDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        rol={editRol}
        permisos={permisos}
        permisosGrouped={permisosGrouped}
      />

      {/* Delete confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro de eliminar el rol <strong>{deleteConfirm?.nombre}</strong>?
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} color="inherit">Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ---- Rol Create/Edit Dialog ----

function RolDialog({
  open,
  onClose,
  rol,
  permisos,
  permisosGrouped,
}: {
  open: boolean;
  onClose: () => void;
  rol: IModuloRol | null;
  permisos: IModuloPermiso[];
  permisosGrouped: Record<string, IModuloPermiso[]>;
}) {
  const createMutation = useCreateRol();
  const updateMutation = useUpdateRol();

  const [nombre, setNombre] = useState('');
  const [codigo, setCodigo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [selectedPermisos, setSelectedPermisos] = useState<Set<string>>(new Set());

  // Reset when dialog opens
  useEffect(() => {
    if (open && rol) {
      setNombre(rol.nombre);
      setCodigo(rol.codigo);
      setDescripcion(rol.descripcion || '');
      const ids = new Set(
        (rol.permisos_data || []).map((p) => p.id_permiso)
      );
      // Fallback: if permisos_data is empty but permisos[] has IDs
      if (ids.size === 0 && rol.permisos?.length) {
        rol.permisos.forEach((pid) => ids.add(pid));
      }
      setSelectedPermisos(ids);
    } else if (open) {
      setNombre('');
      setCodigo('');
      setDescripcion('');
      setSelectedPermisos(new Set());
    }
  }, [open, rol]);

  const handleTogglePermiso = (id: string) => {
    setSelectedPermisos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleModulo = (modulo: string) => {
    const moduloPermisos = permisosGrouped[modulo] || [];
    const allSelected = moduloPermisos.every((p) => selectedPermisos.has(p.id_permiso));
    setSelectedPermisos((prev) => {
      const next = new Set(prev);
      for (const p of moduloPermisos) {
        if (allSelected) next.delete(p.id_permiso);
        else next.add(p.id_permiso);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const allIds = permisos.map((p) => p.id_permiso);
    setSelectedPermisos(new Set(allIds));
  };

  const handleDeselectAll = () => {
    setSelectedPermisos(new Set());
  };

  const handleSave = () => {
    const data = {
      codigo: codigo || nombre.toLowerCase().replace(/\s+/g, '_'),
      nombre,
      descripcion,
      permisos: Array.from(selectedPermisos),
    };

    if (rol) {
      updateMutation.mutate({ id: rol.id_rol, data }, { onSuccess: onClose });
    } else {
      createMutation.mutate(data, { onSuccess: onClose });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{rol ? `Editar Rol: ${rol.nombre}` : 'Nuevo Rol'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Nombre del Rol *"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Código"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              disabled={!!rol?.es_predefinido}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Descripción"
              multiline
              rows={2}
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
            />
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Permisos ({selectedPermisos.size} de {permisos.length} seleccionados)
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={handleSelectAll}>Seleccionar todos</Button>
            <Button size="small" color="warning" onClick={handleDeselectAll}>Quitar todos</Button>
          </Stack>
        </Stack>

        {Object.entries(permisosGrouped).map(([modulo, moduloPermisos]) => {
          const allSelected = moduloPermisos.every((p) => selectedPermisos.has(p.id_permiso));
          const someSelected = moduloPermisos.some((p) => selectedPermisos.has(p.id_permiso));
          const selectedCount = moduloPermisos.filter((p) => selectedPermisos.has(p.id_permiso)).length;

          return (
            <Accordion key={modulo} defaultExpanded={false} variant="outlined" sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={() => handleToggleModulo(modulo)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontWeight={600}>
                        {MODULO_LABELS[modulo] || modulo}
                      </Typography>
                      <Chip
                        label={`${selectedCount}/${moduloPermisos.length}`}
                        size="small"
                        color={allSelected ? 'success' : someSelected ? 'warning' : 'default'}
                      />
                    </Stack>
                  }
                  onClick={(e) => e.stopPropagation()}
                />
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={0}>
                  {moduloPermisos.map((p) => (
                    <Grid item xs={12} md={6} key={p.id_permiso}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedPermisos.has(p.id_permiso)}
                            onChange={() => handleTogglePermiso(p.id_permiso)}
                          />
                        }
                        label={
                          <Tooltip title={p.descripcion || p.codigo} placement="right">
                            <Typography variant="body2">{p.nombre}</Typography>
                          </Tooltip>
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={!nombre || isPending}>
          {isPending ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================================================
// TAB 1: Usuarios y Roles (user-centric)
// ===========================================================================

function TabUsuariosRoles() {
  const { data: rolesRes } = useFetchRoles();
  const asignarMutation = useAsignarRolUsuario();
  const eliminarMutation = useEliminarRolUsuario();

  const roles: IModuloRol[] = (rolesRes as any)?.data?.data || (rolesRes as any)?.data || [];

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Edit dialog state (asignar / cambiar rol al usuario seleccionado)
  const [editUser, setEditUser] = useState<IUsuarioERP | null>(null);
  const [selectedRol, setSelectedRol] = useState('');

  // User detail dialog state
  const [detailUser, setDetailUser] = useState<{ id: number; username: string; nombre: string } | null>(null);

  // Debounce de la busqueda para no disparar fetch en cada keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  // Cargar TODOS los usuarios del ERP paginados, con su rol/area asignado
  const { data: usersRes, isLoading } = useFetchUsuariosERPList({
    q: debouncedSearch,
    page,
    page_size: pageSize,
    with_assignments: true,
  });

  const _r = usersRes as any;
  const usuarios: IUsuarioERP[] =
    _r?.data?.data || (Array.isArray(_r?.data) ? _r.data : []);
  const totalPages = _r?.data?.pages || 1;
  const total = _r?.data?.total || 0;

  const openEditDialog = (user: IUsuarioERP) => {
    setEditUser(user);
    setSelectedRol(user.rol_asignado?.id_rol || '');
  };

  const handleGuardar = () => {
    if (!editUser || !selectedRol) return;
    asignarMutation.mutate(
      {
        usuario_id: editUser.id,
        usuario_username: editUser.username,
        usuario_nombre_completo: editUser.nombre_completo,
        rol: selectedRol,
      },
      {
        onSuccess: () => {
          setEditUser(null);
          setSelectedRol('');
        },
      }
    );
  };

  const handleQuitar = (user: IUsuarioERP) => {
    if (!user.rol_asignado?.id_usuario_rol) return;
    eliminarMutation.mutate(user.rol_asignado.id_usuario_rol);
  };

  return (
    <>
      {/* Buscador + contador */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Buscar por nombre, username o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200, maxWidth: 400 }}
        />
        <Chip label={`${total} usuarios`} color="primary" variant="outlined" />
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          href="/parametrizacion/usuarios/crear"
          target="_blank"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Crear Usuario en ERP
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Usuario</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Área / Depto ERP</TableCell>
              <TableCell>Rol ERP</TableCell>
              <TableCell>Rol Tickets</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : usuarios.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center"><Typography color="text.secondary">Sin resultados</Typography></TableCell></TableRow>
            ) : (
              usuarios.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                      onClick={() => setDetailUser({ id: u.id, username: u.username, nombre: u.nombre_completo })}
                    >
                      {u.nombre_completo}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{u.username}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{u.email || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{u.area_id || u.area || '-'}</Typography>
                    {(u.departamento_id || u.departamento) && (
                      <Typography variant="caption" color="text.secondary">
                        {u.departamento_id || u.departamento}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={u.rol_sistema || u.rol || '-'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {u.rol_asignado ? (
                      <Chip
                        label={u.rol_asignado.nombre}
                        size="small"
                        color="primary"
                      />
                    ) : (
                      <Chip
                        label="Sin asignar"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Ver detalle del usuario">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setDetailUser({ id: u.id, username: u.username, nombre: u.nombre_completo })}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openEditDialog(u)}
                      >
                        {u.rol_asignado ? 'Cambiar' : 'Asignar'}
                      </Button>
                      {u.rol_asignado && (
                        <Tooltip title="Quitar rol">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleQuitar(u)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      {totalPages > 1 && (
        <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <Chip label={`${page} / ${totalPages}`} variant="outlined" />
          <Button size="small" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Siguiente</Button>
        </Stack>
      )}

      {/* Asignar / Cambiar rol dialog */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editUser?.rol_asignado ? 'Cambiar rol de ' : 'Asignar rol a '}
          {editUser?.nombre_completo}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Rol *"
              value={selectedRol}
              onChange={(e) => setSelectedRol(e.target.value)}
            >
              <MenuItem value="">-- Seleccionar --</MenuItem>
              {roles.map((r) => (
                <MenuItem key={r.id_rol} value={r.id_rol}>
                  {r.nombre} ({r.permisos_count ?? r.permisos?.length ?? 0} permisos)
                </MenuItem>
              ))}
            </TextField>
            {editUser && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Datos del usuario en el ERP
                </Typography>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Username</Typography>
                    <Typography variant="body2">{editUser.username}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body2">{editUser.email || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Área ERP</Typography>
                    <Typography variant="body2">{editUser.area_id || editUser.area || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Rol ERP</Typography>
                    <Typography variant="body2">{editUser.rol_sistema || editUser.rol || '-'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditUser(null)} color="inherit">Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleGuardar}
            disabled={!selectedRol || asignarMutation.isPending}
          >
            {asignarMutation.isPending ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User detail */}
      <UserDetailDialog
        open={!!detailUser}
        onClose={() => setDetailUser(null)}
        userId={detailUser?.id || null}
        username={detailUser?.username || ''}
        nombreCompleto={detailUser?.nombre || ''}
      />
    </>
  );
}

// ===========================================================================
// TAB 2: Usuarios y Áreas (user-centric)
// ===========================================================================

function TabUsuariosAreas() {
  const { data: areasRes } = useFetchTicketAreas();
  const asignarMutation = useAsignarAreaUsuario();
  const actualizarMutation = useActualizarAreaUsuario();
  const eliminarMutation = useEliminarAreaUsuario();

  const areas = (areasRes as any)?.data?.data || (areasRes as any)?.data || [];

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Multi-area: el dialog gestiona TODAS las areas del usuario seleccionado.
  // newAreaToAdd y newAreaResponsable son el form de "agregar otra area".
  const [editUser, setEditUser] = useState<IUsuarioERP | null>(null);
  const [newAreaToAdd, setNewAreaToAdd] = useState('');
  const [newAreaResponsable, setNewAreaResponsable] = useState(false);
  // Check "Configuración": solo acceso al árbol del área en Catálogos
  // (apertura/cierre), sin ser responsable: no recibe tickets, no ve la
  // bandeja del área ni aparece como asignable. Pensado para admin TI.
  const [newAreaConfiguracion, setNewAreaConfiguracion] = useState(false);

  const [detailUser, setDetailUser] = useState<{ id: number; username: string; nombre: string } | null>(null);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const { data: usersRes, isLoading } = useFetchUsuariosERPList({
    q: debouncedSearch,
    page,
    page_size: pageSize,
    with_assignments: true,
  });

  const _r = usersRes as any;
  const usuarios: IUsuarioERP[] =
    _r?.data?.data || (Array.isArray(_r?.data) ? _r.data : []);
  const totalPages = _r?.data?.pages || 1;
  const total = _r?.data?.total || 0;

  // Helper: lista de areas del usuario (fallback a area_asignada singular si
  // el backend viejo aun no expone areas_asignadas).
  const getAreasDeUsuario = (u: IUsuarioERP): any[] => {
    const u2 = u as any;
    if (Array.isArray(u2.areas_asignadas)) return u2.areas_asignadas;
    if (u.area_asignada) return [u.area_asignada];
    return [];
  };

  // Cuando se reabre el dialog (al guardar y refetch), el usuario en estado
  // local queda con su snapshot viejo. Sincronizamos con la lista fresca.
  const editUserFresh: IUsuarioERP | null = editUser
    ? (usuarios.find((u) => u.id === editUser.id) ?? editUser)
    : null;
  const areasDelUsuario = editUserFresh ? getAreasDeUsuario(editUserFresh) : [];
  const idsYaAsignados = new Set(areasDelUsuario.map((a: any) => a.id_area));

  const openEditDialog = (user: IUsuarioERP) => {
    setEditUser(user);
    setNewAreaToAdd('');
    setNewAreaResponsable(false);
    setNewAreaConfiguracion(false);
  };

  const handleAgregarArea = () => {
    if (!editUserFresh || !newAreaToAdd) return;
    asignarMutation.mutate(
      {
        usuario_id: editUserFresh.id,
        usuario_username: editUserFresh.username,
        usuario_nombre_completo: editUserFresh.nombre_completo,
        area: newAreaToAdd,
        es_responsable: newAreaResponsable,
        es_configuracion: newAreaConfiguracion,
      },
      {
        onSuccess: () => {
          setNewAreaToAdd('');
          setNewAreaResponsable(false);
          setNewAreaConfiguracion(false);
        },
      }
    );
  };

  const handleToggleResponsable = (
    id_usuario_area: string,
    nuevoValor: boolean,
  ) => {
    actualizarMutation.mutate({
      id_usuario_area,
      es_responsable: nuevoValor,
    });
  };

  const handleToggleConfiguracion = (
    id_usuario_area: string,
    nuevoValor: boolean,
  ) => {
    actualizarMutation.mutate({
      id_usuario_area,
      es_configuracion: nuevoValor,
    });
  };

  const handleQuitarArea = (id_usuario_area: string) => {
    eliminarMutation.mutate(id_usuario_area);
  };

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Buscar por nombre, username o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200, maxWidth: 400 }}
        />
        <Chip label={`${total} usuarios`} color="primary" variant="outlined" />
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          href="/parametrizacion/usuarios/crear"
          target="_blank"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Crear Usuario en ERP
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Usuario</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Área ERP</TableCell>
              <TableCell>Área Tickets</TableCell>
              <TableCell>Responsable / Config.</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : usuarios.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center"><Typography color="text.secondary">Sin resultados</Typography></TableCell></TableRow>
            ) : (
              usuarios.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main', textDecoration: 'underline' } }}
                      onClick={() => setDetailUser({ id: u.id, username: u.username, nombre: u.nombre_completo })}
                    >
                      {u.nombre_completo}
                    </Typography>
                  </TableCell>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.email || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{u.area_id || u.area || '-'}</Typography>
                    {(u.departamento_id || u.departamento) && (
                      <Typography variant="caption" color="text.secondary">
                        {u.departamento_id || u.departamento}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const areasU = getAreasDeUsuario(u);
                      if (areasU.length === 0) {
                        return (
                          <Chip label="Sin asignar" size="small" color="default" variant="outlined" />
                        );
                      }
                      return (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
                          {areasU.map((a: any) => (
                            <Chip
                              key={a.id_usuario_area}
                              label={a.nombre}
                              size="small"
                              sx={{ bgcolor: a.color || '#9e9e9e', color: 'white' }}
                            />
                          ))}
                        </Stack>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const areasU = getAreasDeUsuario(u);
                      const responsableDe = areasU.filter((a: any) => a.es_responsable);
                      const configuraDe = areasU.filter((a: any) => a.es_configuracion);
                      if (responsableDe.length === 0 && configuraDe.length === 0) return null;
                      return (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {responsableDe.length > 0 && (
                            <Tooltip title={`Responsable de: ${responsableDe.map((a: any) => a.nombre).join(', ')}`}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <CheckCircleIcon color="success" fontSize="small" />
                                <Typography variant="caption" fontWeight={600}>
                                  {responsableDe.length > 1 ? `(${responsableDe.length})` : ''}
                                </Typography>
                              </Stack>
                            </Tooltip>
                          )}
                          {configuraDe.length > 0 && (
                            <Tooltip title={`Configura el árbol de: ${configuraDe.map((a: any) => a.nombre).join(', ')}`}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <AccountTreeIcon color="info" fontSize="small" />
                                <Typography variant="caption" fontWeight={600}>
                                  {configuraDe.length > 1 ? `(${configuraDe.length})` : ''}
                                </Typography>
                              </Stack>
                            </Tooltip>
                          )}
                        </Stack>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Ver detalle del usuario">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setDetailUser({ id: u.id, username: u.username, nombre: u.nombre_completo })}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openEditDialog(u)}
                      >
                        {getAreasDeUsuario(u).length > 0 ? 'Gestionar áreas' : 'Asignar área'}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <Chip label={`${page} / ${totalPages}`} variant="outlined" />
          <Button size="small" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Siguiente</Button>
        </Stack>
      )}

      {/* Gestionar areas (multi) - el usuario puede pertenecer y ser
          responsable de N areas simultaneamente. Cada accion (toggle
          responsable / quitar / agregar) hace una llamada inmediata al
          backend y la tabla se refresca via invalidateQueries. */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Gestionar áreas de {editUserFresh?.nombre_completo}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Lista de areas actualmente asignadas */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Áreas asignadas ({areasDelUsuario.length})
              </Typography>
              {areasDelUsuario.length === 0 ? (
                <Alert severity="info" variant="outlined">
                  Este usuario no tiene áreas asignadas. Agrega la primera abajo.
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {areasDelUsuario.map((a: any) => (
                    <Paper key={a.id_usuario_area} variant="outlined" sx={{ p: 1.25 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip
                          label={a.nombre}
                          size="small"
                          sx={{ bgcolor: a.color || '#9e9e9e', color: 'white', minWidth: 100 }}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Responsable del área: recibe los tickets asignados/escalados al área y ve su bandeja. También puede configurar el árbol de apertura/cierre.">
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={!!a.es_responsable}
                                onChange={(e) =>
                                  handleToggleResponsable(a.id_usuario_area, e.target.checked)
                                }
                                disabled={actualizarMutation.isPending}
                              />
                            }
                            label="Responsable"
                          />
                        </Tooltip>
                        <Tooltip title="Solo configuración: puede gestionar el árbol de apertura/cierre del área en Catálogos, pero NO es responsable — no recibe tickets, no ve la bandeja del área ni aparece para asignación. Pensado para administradores de TI.">
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                color="info"
                                checked={!!a.es_configuracion}
                                onChange={(e) =>
                                  handleToggleConfiguracion(a.id_usuario_area, e.target.checked)
                                }
                                disabled={actualizarMutation.isPending}
                              />
                            }
                            label="Configuración"
                          />
                        </Tooltip>
                        <Tooltip title="Quitar área">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleQuitarArea(a.id_usuario_area)}
                            disabled={eliminarMutation.isPending}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>

            <Divider />

            {/* Agregar otra area */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Agregar otra área
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Área"
                  value={newAreaToAdd}
                  onChange={(e) => setNewAreaToAdd(e.target.value)}
                >
                  <MenuItem value="">-- Seleccionar --</MenuItem>
                  {areas
                    .filter((a: any) => !idsYaAsignados.has(a.id_area))
                    .map((a: any) => (
                      <MenuItem key={a.id_area} value={a.id_area}>{a.nombre}</MenuItem>
                    ))}
                </TextField>
                <Tooltip title="Responsable del área: recibe los tickets asignados/escalados al área y ve su bandeja.">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={newAreaResponsable}
                        onChange={(e) => setNewAreaResponsable(e.target.checked)}
                      />
                    }
                    label="Responsable"
                  />
                </Tooltip>
                <Tooltip title="Solo configuración: gestiona el árbol de apertura/cierre del área en Catálogos sin recibir tickets ni ver su bandeja (admin TI).">
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="info"
                        checked={newAreaConfiguracion}
                        onChange={(e) => setNewAreaConfiguracion(e.target.checked)}
                      />
                    }
                    label="Configuración"
                  />
                </Tooltip>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAgregarArea}
                  disabled={!newAreaToAdd || asignarMutation.isPending}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {asignarMutation.isPending ? '...' : 'Agregar'}
                </Button>
              </Stack>
              {areas.length === idsYaAsignados.size && areas.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Este usuario ya está asignado a todas las áreas disponibles.
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Datos del usuario en el ERP */}
            {editUserFresh && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Datos del usuario en el ERP
                </Typography>
                <Grid container spacing={1} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Username</Typography>
                    <Typography variant="body2">{editUserFresh.username}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Email</Typography>
                    <Typography variant="body2">{editUserFresh.email || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Área ERP</Typography>
                    <Typography variant="body2">{editUserFresh.area_id || editUserFresh.area || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Rol ERP</Typography>
                    <Typography variant="body2">{editUserFresh.rol_sistema || editUserFresh.rol || '-'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditUser(null)} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <UserDetailDialog
        open={!!detailUser}
        onClose={() => setDetailUser(null)}
        userId={detailUser?.id || null}
        username={detailUser?.username || ''}
        nombreCompleto={detailUser?.nombre || ''}
      />
    </>
  );
}

// ===========================================================================
// TAB 3: Módulos del Sistema
// ===========================================================================

const CATEGORY_LABELS: Record<string, string> = {
  ADMIN: 'Administración',
  MANTENIMIENTO: 'Mantenimiento',
  VENTAS: 'Ventas',
  VENTAS_PYMES: 'Ventas PYMES',
  SUPERVISOR_PYMES: 'Supervisión PYMES',
  SOPORTE_PYMES: 'Soporte PYMES',
  SERVICIOS: 'Servicios',
  SUPERVISION_VENTAS: 'Supervisión Ventas',
  OPERACIONES: 'Operaciones',
  ACTIVACIONES: 'Activaciones',
  CONFIGURACION_RED: 'Configuración de Red',
  TECNICO: 'Técnico',
  CLIENTES: 'Clientes',
  USUARIOS: 'Usuarios',
  INVENTARIO: 'Inventario',
  COMPRAS: 'Compras',
  ACTIVOS: 'Activos',
  CONTABILIDAD: 'Contabilidad',
  SOPORTE_TECNICO: 'Soporte Técnico',
  TICKETS_SOPORTE_CLIENTES: 'Tickets Soporte Clientes',
  TICKETS_CATALOGOS: 'Catálogos de Tickets',
  CAMPANAS: 'Campañas',
  VISITAS: 'Visitas',
  NOMINA: 'Nómina',
  CATALOGO_PRODUCTO: 'Catálogo de Producto',
  CONFIGURACION_INVENTARIO: 'Configuración de Inventario',
  DOCUMENTACION: 'Documentación',
  RECAUDACIONES: 'Recaudaciones',
  SCORPORATIVOS: 'Corporativos',
  NOC: 'NOC',
  SOPORTE_CORPORATIVO: 'Soporte Corporativo',
  MARKETING: 'Marketing',
  SOLUCIONES_DIGITALES: 'Soluciones Digitales',
  REPORTES: 'Reportes',
};

// Descripción legible por módulo (se muestra en el tooltip del checkbox).
// Si un módulo no está aquí, cae al string del módulo (comportamiento previo).
const MODULE_DESCRIPTIONS: Record<string, string> = {
  // Flujo de Visitas Técnicas
  'VISITAS.REQUIERE_VISITA': 'Habilita el check "¿Requiere Visita?" al gestionar un ticket → genera la visita técnica.',
  'VISITAS.TICKETS_PENDIENTES_ASIGNACION': 'Ver y asignar a técnico las visitas pendientes de asignación.',
  'VISITAS.AGENDA_DIARIA': 'Ver la agenda diaria de visitas.',
  'VISITAS.FINALIZADAS': 'Ver el tab "Calidad de Visitas" (revisión de visitas finalizadas + KPIs).',
  'TICKETS_SOPORTE_CLIENTES.AGENDA_TECNICOS': 'Calendario de visitas por técnico (Mes/Semana/Día) con filtros y detalle.',
  'TICKETS_SOPORTE_CLIENTES.NUEVO_TICKET': 'Crear tickets de soporte al cliente.',
  'TICKETS_SOPORTE_CLIENTES.MIS_TICKETS': 'Ver y gestionar los tickets asignados.',
  'TICKETS_SOPORTE_CLIENTES.CATALOGOS': 'Acceso al módulo de catálogos de tickets.',
  'TICKETS_SOPORTE_CLIENTES.GESTION_ACCESOS': 'Administrar módulos/permisos de los usuarios.',
  'ACTIVACIONES.VISITA': 'Revisar/aprobar la visita finalizada y liberar la Orden de Trabajo del técnico.',
  'OPERACIONES.ACTIVACION.INSTALACIONES.INSTALACIONES': 'Aprobar instalaciones/activaciones.',
  'NOC.ORDENES_DE_TRABAJO': 'Ver y gestionar las órdenes de trabajo de visita.',
  'OPERACIONES.ORDENES_SERVICIO_GENERAL': 'Ver el listado general de órdenes de servicio creadas.',
  'TECNICO.VISITAS': 'El técnico ejecuta y finaliza la visita en campo.',
  // Catálogos de Tickets (tabs)
  'TICKETS_CATALOGOS.ESTADOS': 'Configurar el catálogo de Estados de ticket.',
  'TICKETS_CATALOGOS.PRIORIDADES': 'Configurar Prioridades / SLA de tickets.',
  'TICKETS_CATALOGOS.AREAS': 'Configurar las Áreas de tickets.',
  'TICKETS_CATALOGOS.CANALES': 'Configurar los Canales de ingreso de tickets.',
  'TICKETS_CATALOGOS.TIPOS': 'Configurar los Tipos de ticket (incluye "requiere visita" por defecto).',
  'TICKETS_CATALOGOS.CATEGORIAS': 'Configurar el árbol de Categorías de tickets.',
  'TICKETS_CATALOGOS.SUBCATEGORIAS': 'Configurar las Subcategorías de tickets.',
  'TICKETS_CATALOGOS.ARBOL_CIERRE': 'Configurar el Árbol de Cierre (motivos de cierre).',
  'TICKETS_CATALOGOS.CONFIGURACION_ARBOL': 'Configuración avanzada del árbol (legacy por área).',
  'TICKETS_CATALOGOS.ADMINISTRACION': 'Ver el tab "Administración" y configurar conceptos/parámetros de visita.',
  'TICKETS_CATALOGOS.ADMINISTRACION_VISITAS': 'Sub-tab "Visitas" de Administración: conceptos, IVA, puntos de red, tipos de instalación y métodos de pago.',
  'TICKETS_CATALOGOS.ADMINISTRACION_TRASLADOS': 'Sub-tab "Traslados" de Administración: descuento al pagar un traslado con FibraPoints.',
};

function formatModuleName(name: string): string {
  return name
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

function groupModulesByCategory(modules: string[]): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  for (const mod of modules) {
    const dotIndex = mod.indexOf('.');
    if (dotIndex === -1) {
      if (!groups['OTROS']) groups['OTROS'] = [];
      groups['OTROS'].push(mod);
      continue;
    }
    const category = mod.substring(0, dotIndex);
    if (!groups[category]) groups[category] = [];
    groups[category].push(mod);
  }
  const sorted: Record<string, string[]> = {};
  const sortedKeys = Object.keys(groups).sort((a, b) => {
    const la = CATEGORY_LABELS[a] || a;
    const lb = CATEGORY_LABELS[b] || b;
    return la.localeCompare(lb);
  });
  for (const k of sortedKeys) {
    sorted[k] = groups[k].sort();
  }
  return sorted;
}

function TabModulos() {
  const updateModulosMutation = useUpdateUsuarioModulos();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [selectedUser, setSelectedUser] = useState<IUsuarioERP | null>(null);
  const [selectedModulos, setSelectedModulos] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);

  // Debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  // Lista paginada de TODOS los usuarios del ERP (sin enrichment de tickets,
  // los modulos vienen de api_extendedgroup en base_erp).
  const { data: usersRes, isLoading } = useFetchUsuariosERPList({
    q: debouncedSearch,
    page,
    page_size: pageSize,
  });
  const _r = usersRes as any;
  const usuarios: IUsuarioERP[] =
    _r?.data?.data || (Array.isArray(_r?.data) ? _r.data : []);
  const totalPages = _r?.data?.pages || 1;
  const total = _r?.data?.total || 0;

  // Catalogo completo de modulos del sistema (para mostrar checkboxes)
  const { data: modulosSistemaRes, isLoading: isLoadingModulos } = useFetchModulosSistema();
  const allModulos: string[] = (modulosSistemaRes as any)?.data || [];

  // Modulos actuales del usuario seleccionado en el dialog
  const { data: userModulosRes, isLoading: isLoadingUserModulos } = useFetchUsuarioModulos(
    selectedUser?.id || 0,
    !!selectedUser
  );
  const userModulosData = (userModulosRes as any)?.data?.data || (userModulosRes as any)?.data || null;

  // Group modules by category
  const grouped = useMemo(() => groupModulesByCategory(allModulos), [allModulos]);

  // Cuando llegan los modulos del usuario, los meto en el set seleccionado
  useEffect(() => {
    if (userModulosData?.modulos) {
      setSelectedModulos(new Set(userModulosData.modulos));
      setHasChanges(false);
    }
  }, [userModulosData]);

  // Cuando se cierra el dialog (selectedUser=null), reset
  useEffect(() => {
    if (!selectedUser) {
      setSelectedModulos(new Set());
      setHasChanges(false);
    }
  }, [selectedUser]);

  const handleToggleModulo = (mod: string) => {
    setSelectedModulos((prev) => {
      const next = new Set(prev);
      if (next.has(mod)) next.delete(mod);
      else next.add(mod);
      return next;
    });
    setHasChanges(true);
  };

  const handleToggleCategory = (category: string) => {
    const categoryModules = grouped[category] || [];
    const allSelected = categoryModules.every((m) => selectedModulos.has(m));
    setSelectedModulos((prev) => {
      const next = new Set(prev);
      for (const m of categoryModules) {
        if (allSelected) next.delete(m);
        else next.add(m);
      }
      return next;
    });
    setHasChanges(true);
  };

  const handleSelectAll = () => {
    setSelectedModulos(new Set(allModulos));
    setHasChanges(true);
  };

  const handleDeselectAll = () => {
    setSelectedModulos(new Set());
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!selectedUser) return;
    updateModulosMutation.mutate({
      userId: selectedUser.id,
      modulos: Array.from(selectedModulos),
    });
    setHasChanges(false);
  };

  return (
    <>
      <Alert severity="info" sx={{ mb: 2 }}>
        Los módulos del sistema controlan qué opciones ve cada usuario en el menú de navegación.
        Haz clic en "Editar Módulos" en cualquier fila para gestionar los módulos visibles de ese usuario.
      </Alert>

      {/* Buscador */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Buscar por nombre, username o email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200, maxWidth: 400 }}
        />
        <Chip label={`${total} usuarios`} color="primary" variant="outlined" />
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Usuario</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Área ERP</TableCell>
              <TableCell>Rol ERP</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : usuarios.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center"><Typography color="text.secondary">Sin resultados</Typography></TableCell></TableRow>
            ) : (
              usuarios.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{u.nombre_completo}</Typography>
                  </TableCell>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.email || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{u.area_id || u.area || '-'}</Typography>
                    {(u.departamento_id || u.departamento) && (
                      <Typography variant="caption" color="text.secondary">
                        {u.departamento_id || u.departamento}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={u.rol_sistema || u.rol || '-'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ViewModuleIcon />}
                      onClick={() => setSelectedUser(u)}
                    >
                      Editar Módulos
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <Chip label={`${page} / ${totalPages}`} variant="outlined" />
          <Button size="small" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Siguiente</Button>
        </Stack>
      )}

      {/* Editor de modulos en dialog */}
      <Dialog
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Stack>
              <Typography variant="h6" fontWeight={700}>
                Módulos de {selectedUser?.nombre_completo}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedUser?.username} · {selectedUser?.email}
              </Typography>
            </Stack>
            {selectedUser && (
              <Chip
                label={`${selectedModulos.size} / ${allModulos.length} módulos`}
                color="primary"
                variant="outlined"
              />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {(isLoadingModulos || isLoadingUserModulos) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button size="small" onClick={handleSelectAll}>Seleccionar todos</Button>
                <Button size="small" color="warning" onClick={handleDeselectAll}>Quitar todos</Button>
              </Stack>
              <Stack spacing={1}>
                {Object.entries(grouped).map(([category, categoryModules]) => {
                  const allSelected = categoryModules.every((m) => selectedModulos.has(m));
                  const someSelected = categoryModules.some((m) => selectedModulos.has(m));
                  const selectedCount = categoryModules.filter((m) => selectedModulos.has(m)).length;
                  return (
                    <Accordion key={category} variant="outlined" defaultExpanded={false}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected && !allSelected}
                              onChange={() => handleToggleCategory(category)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          }
                          label={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography fontWeight={600}>
                                {CATEGORY_LABELS[category] || formatModuleName(category)}
                              </Typography>
                              <Chip
                                label={`${selectedCount}/${categoryModules.length}`}
                                size="small"
                                color={allSelected ? 'success' : someSelected ? 'warning' : 'default'}
                              />
                            </Stack>
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={0}>
                          {categoryModules.map((mod) => {
                            const shortName = mod.includes('.') ? mod.split('.').slice(1).join('.') : mod;
                            return (
                              <Grid item xs={12} sm={6} md={4} key={mod}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={selectedModulos.has(mod)}
                                      onChange={() => handleToggleModulo(mod)}
                                    />
                                  }
                                  label={
                                    <Tooltip title={MODULE_DESCRIPTIONS[mod] || mod} placement="right">
                                      <Typography variant="body2">
                                        {formatModuleName(shortName)}
                                      </Typography>
                                    </Tooltip>
                                  }
                                />
                              </Grid>
                            );
                          })}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSelectedUser(null)} color="inherit">Cerrar</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || updateModulosMutation.isPending}
          >
            {updateModulosMutation.isPending ? 'Guardando...' : 'Guardar Módulos'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
